import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { ArrowRight, Sparkles, ShieldCheck, Zap, Palette, Code2, PenTool, TrendingUp, Video } from "lucide-react";

const CATEGORIES = [
  { icon: Code2, name: "Programación", count: "1.2k+" },
  { icon: Palette, name: "Diseño", count: "890+" },
  { icon: PenTool, name: "Redacción", count: "540+" },
  { icon: TrendingUp, name: "Marketing", count: "720+" },
  { icon: Video, name: "Video & Audio", count: "310+" },
  { icon: Sparkles, name: "IA & Datos", count: "460+" },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1704770064557-416292d1f4bd"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover opacity-[0.08] dark:opacity-[0.15]"
          />
          <div className="grain-overlay" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs tracking-widest uppercase font-bold mb-6" data-testid="hero-badge">
                <Sparkles className="w-3 h-3" /> Marketplace de tareas digitales
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight leading-[1.02] mb-6">
                Contrata talento.<br />
                <span className="text-primary">Gana dinero</span> extra.
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed mb-8">
                La plataforma donde empresas publican proyectos y personas talentosas los completan a cambio de dinero. Diseño, código, marketing y más — todo en un mismo lugar.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/register">
                  <Button className="rounded-full h-12 px-7 text-base" data-testid="hero-cta-primary">
                    Empezar gratis <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button variant="outline" className="rounded-full h-12 px-7 text-base" data-testid="hero-cta-secondary">
                    Explorar proyectos
                  </Button>
                </Link>
              </div>
            </div>
            <div className="lg:col-span-4 hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { k: "Proyectos", v: "12k+" },
                  { k: "Freelancers", v: "8.4k" },
                  { k: "Empresas", v: "2.1k" },
                  { k: "Pagado", v: "$4M+" },
                ].map((s) => (
                  <div key={s.k} className="p-5 rounded-lg border border-border bg-card">
                    <div className="text-3xl font-heading font-bold tracking-tight">{s.v}</div>
                    <div className="text-xs tracking-widest uppercase text-muted-foreground mt-1">{s.k}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
            <div>
              <div className="text-xs tracking-widest uppercase text-primary font-bold mb-3">Categorías</div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">Encuentra tu área</h2>
            </div>
            <Link to="/projects" className="text-sm text-muted-foreground hover:text-primary transition-colors">Ver todas →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((c) => (
              <Link key={c.name} to={`/projects?category=${encodeURIComponent(c.name)}`} data-testid={`category-${c.name}`}
                className="p-6 rounded-lg border border-border bg-card lift group">
                <c.icon className="w-6 h-6 mb-4 text-primary" />
                <div className="font-medium mb-1">{c.name}</div>
                <div className="text-xs text-muted-foreground">{c.count} proyectos</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 relative">
            <img src="https://images.unsplash.com/photo-1638262052640-82e94d64664a" alt="Acuerdo profesional"
              className="rounded-lg w-full object-cover aspect-[4/5]" />
          </div>
          <div className="lg:col-span-7">
            <div className="text-xs tracking-widest uppercase text-primary font-bold mb-3">Cómo funciona</div>
            <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight mb-10">Tres pasos, cero fricción.</h2>
            <div className="space-y-6">
              {[
                { n: "01", t: "Publica o postúlate", d: "Las empresas publican el proyecto con presupuesto. Los freelancers envían propuestas competitivas." },
                { n: "02", t: "Elige y colabora", d: "Revisa perfiles, chatea en tiempo real y selecciona a la persona ideal para tu proyecto." },
                { n: "03", t: "Paga seguro con Stripe", d: "El pago queda protegido y se libera al freelancer una vez completado el proyecto." },
              ].map((step) => (
                <div key={step.n} className="flex gap-6 border-b border-border pb-6 last:border-0">
                  <div className="font-heading font-bold text-3xl text-primary tabular-nums shrink-0 w-14">{step.n}</div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold mb-1">{step.t}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST */}
      <section className="py-20 md:py-28 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, t: "Pagos protegidos", d: "Tu dinero queda en escrow hasta que el trabajo esté completado a tu satisfacción." },
              { icon: Zap, t: "Rapidez real", d: "Publica en minutos y recibe propuestas de talento verificado en horas, no días." },
              { icon: Sparkles, t: "Sin comisiones ocultas", d: "Precios transparentes. Sabes exactamente cuánto pagas y cuánto ganas." },
            ].map((f) => (
              <div key={f.t} className="p-8 rounded-lg border border-border bg-card">
                <f.icon className="w-6 h-6 text-primary mb-4" />
                <h3 className="font-heading text-xl font-semibold mb-2">{f.t}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <h2 className="font-heading text-4xl md:text-6xl font-bold tracking-tight mb-6">
            Empieza a ganar hoy.
          </h2>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            Únete a TaskLink y transforma tu talento digital en ingresos reales — o encuentra la persona perfecta para tu próximo proyecto.
          </p>
          <div className="flex justify-center gap-3 flex-wrap">
            <Link to="/register">
              <Button className="rounded-full h-12 px-8 text-base" data-testid="footer-cta-register">
                Crear cuenta <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/projects">
              <Button variant="outline" className="rounded-full h-12 px-8 text-base" data-testid="footer-cta-explore">
                Ver proyectos
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">© 2026 TaskLink. Todos los derechos reservados.</div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground">Marketplace de tareas digitales</div>
        </div>
      </footer>
    </div>
  );
}
